import { CaptureError } from '../contracts';

/**
 * getDisplayMedia exists in Firefox/Safari without audio capture. There is no
 * prompt-free API for detecting that distinction. Offer tab audio only on
 * desktop Chromium; still validate the returned tracks after the picker.
 * Compatibility: https://github.com/mdn/browser-compat-data/blob/main/api/MediaDevices.json
 */
export function tabAudioSupported(userAgent: string): boolean {
  return (
    /(?:Chrome|Chromium)\//u.test(userAgent) && !/Android|Mobile|iPhone|iPad/iu.test(userAgent)
  );
}

// Audio-only getDisplayMedia is forbidden by the browser API. Request a tab
// through its sharing picker, then immediately discard its video. These are
// picker hints, not enforcement: prepareTabAudio also checks the selection.
export const TAB_AUDIO_OPTIONS: DisplayMediaStreamOptions & {
  monitorTypeSurfaces: 'exclude';
  selfBrowserSurface: 'exclude';
  systemAudio: 'exclude';
  surfaceSwitching: 'exclude';
  windowAudio: 'exclude';
} = {
  video: { displaySurface: 'browser' },
  // Shared audio has already been processed by the call. Speech filtering can
  // suppress remote voices or non-speech audio a second time.
  audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
  monitorTypeSurfaces: 'exclude',
  selfBrowserSurface: 'exclude',
  systemAudio: 'exclude',
  surfaceSwitching: 'exclude',
  windowAudio: 'exclude',
};

/** Never hand video, desktop audio or a silent selection to the capture graph. */
export function prepareTabAudio(stream: MediaStream): MediaStream {
  const videos = stream.getVideoTracks();
  const wrongSurface = videos.some((track) => {
    const surface = track.getSettings().displaySurface;
    return surface && surface !== 'browser';
  });
  for (const track of videos) {
    track.stop();
    stream.removeTrack(track);
  }
  if (wrongSurface || stream.getAudioTracks().length === 0) {
    for (const track of stream.getTracks()) track.stop();
    throw new CaptureError('share_no_audio');
  }
  return stream;
}
